import React, {useState, useRef} from "react";
import {Modal, Button, Alert, ProgressBar, Table, Badge} from "react-bootstrap";
import {
  FaUpload,
  FaDownload,
  FaCheck,
  FaExclamationTriangle,
  FaFileExcel,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import {HelpSection} from "./HelpTooltip";

const BulkImportModal = ({show, onHide, onImport, organizations}) => {
  const [importStep, setImportStep] = useState(1); // 1: Upload, 2: Preview, 3: Processing
  const [validationResults, setValidationResults] = useState({
    valid: [],
    invalid: [],
  });
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const requiredColumns = ["nombre", "apellido", "telefono", "email"];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, {type: "binary"});
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        if (data.length === 0) {
          alert("El archivo está vacío o no tiene el formato correcto.");
          return;
        }

        validateData(data);
        setImportStep(2);
      } catch (error) {
        alert(
          "Error al leer el archivo. Asegúrate de que sea un archivo Excel válido."
        );
        console.error("Error:", error);
      }
    };
    reader.readAsBinaryString(file);
  };

  const validateData = (data) => {
    const valid = [];
    const invalid = [];

    data.forEach((row, index) => {
      const errors = [];
      const normalizedRow = {};

      // Normalizar nombres de columnas (convertir a minúsculas y quitar espacios)
      Object.keys(row).forEach((key) => {
        const normalizedKey = key.toLowerCase().trim();
        normalizedRow[normalizedKey] = row[key];
      });

      // Validar campos requeridos
      requiredColumns.forEach((col) => {
        if (
          !normalizedRow[col] ||
          normalizedRow[col].toString().trim() === ""
        ) {
          errors.push(`Falta ${col}`);
        }
      });

      // Validar formato de email
      if (normalizedRow.email && !/\S+@\S+\.\S+/.test(normalizedRow.email)) {
        errors.push("Email inválido");
      }

      // Validar formato de teléfono (básico)
      if (
        normalizedRow.telefono &&
        !/^\+?[\d\s-()]{8,}$/.test(normalizedRow.telefono)
      ) {
        errors.push("Teléfono inválido");
      }

      const processedRow = {
        ...normalizedRow,
        rowNumber: index + 2, // +2 porque Excel empieza en 1 y hay header
        errors,
      };

      if (errors.length === 0) {
        valid.push(processedRow);
      } else {
        invalid.push(processedRow);
      }
    });

    setValidationResults({valid, invalid});
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        nombre: "Juan",
        apellido: "Pérez",
        telefono: "+56912345678",
        email: "juan.perez@email.com",
        rut: "12.345.678-9",
        organizacion: "Transportes ABC",
      },
      {
        nombre: "María",
        apellido: "González",
        telefono: "+56987654321",
        email: "maria.gonzalez@email.com",
        rut: "98.765.432-1",
        organizacion: "Logística XYZ",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla Contactos");
    XLSX.writeFile(workbook, "Plantilla_Importacion_Contactos.xlsx");
  };

  const handleImport = async () => {
    if (validationResults.valid.length === 0) {
      alert("No hay contactos válidos para importar.");
      return;
    }

    setProcessing(true);
    setImportStep(3);
    setProgress(0);

    // Simular procesamiento con progreso
    const totalContacts = validationResults.valid.length;
    const processedContacts = [];

    for (let i = 0; i < totalContacts; i++) {
      const contact = validationResults.valid[i];

      // Simular tiempo de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Convertir a formato de la aplicación
      const newContact = {
        id: `imported-${Date.now()}-${i}`,
        firstName: contact.nombre,
        lastName: contact.apellido,
        phone: contact.telefono,
        email: contact.email,
        rut: contact.rut || "",
        transportista: contact.organizacion || "Sin organización",
      };

      processedContacts.push(newContact);
      setProgress(((i + 1) / totalContacts) * 100);
    }

    // Llamar al callback con los contactos procesados
    onImport(processedContacts);

    setTimeout(() => {
      setProcessing(false);
      handleClose();
    }, 1000);
  };

  const handleClose = () => {
    setImportStep(1);
    setValidationResults({valid: [], invalid: []});
    setProcessing(false);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="me-2 text-primary" />
          Importación Masiva de Contactos
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {importStep === 1 && (
          <>
            <HelpSection
              title="¿Cómo funciona la importación masiva?"
              defaultExpanded={true}
            >
              <p className="mb-2">
                Puedes importar múltiples contactos desde un archivo Excel
                (.xlsx) de una sola vez. Esto es útil cuando tienes listas
                existentes en otros sistemas.
              </p>
              <p className="mb-2">
                <strong>Pasos:</strong>
              </p>
              <ol className="mb-2">
                <li>Descarga la plantilla de ejemplo</li>
                <li>Completa tus datos en el formato correcto</li>
                <li>Sube el archivo y revisa la vista previa</li>
                <li>Confirma la importación</li>
              </ol>
              <Alert variant="warning" className="small mb-0">
                <strong>Importante:</strong> Los contactos duplicados (mismo
                email o teléfono) serán actualizados automáticamente.
              </Alert>
            </HelpSection>

            <div className="text-center py-4">
              <Button
                variant="outline-success"
                size="lg"
                onClick={handleDownloadTemplate}
                className="me-3 mb-3"
              >
                <FaDownload className="me-2" />
                Descargar Plantilla
              </Button>

              <div className="mb-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xlsx,.xls"
                  style={{display: "none"}}
                />
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FaFileExcel className="me-2" />
                  Seleccionar Archivo Excel
                </Button>
              </div>

              <small className="text-muted">
                Formatos soportados: .xlsx, .xls
              </small>
            </div>

            <Alert variant="info">
              <h6 className="fw-bold">Columnas requeridas:</h6>
              <ul className="mb-2">
                <li>
                  <strong>nombre:</strong> Nombre del contacto
                </li>
                <li>
                  <strong>apellido:</strong> Apellido del contacto
                </li>
                <li>
                  <strong>telefono:</strong> Número de WhatsApp (ej:
                  +56912345678)
                </li>
                <li>
                  <strong>email:</strong> Correo electrónico
                </li>
              </ul>
              <h6 className="fw-bold">Columnas opcionales:</h6>
              <ul className="mb-0">
                <li>
                  <strong>rut:</strong> RUT del contacto
                </li>
                <li>
                  <strong>organizacion:</strong> Empresa donde trabaja
                </li>
              </ul>
            </Alert>
          </>
        )}

        {importStep === 2 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Vista Previa de Importación</h5>
              <div>
                <Badge bg="success" className="me-2">
                  {validationResults.valid.length} Válidos
                </Badge>
                <Badge bg="danger">
                  {validationResults.invalid.length} Con errores
                </Badge>
              </div>
            </div>

            {validationResults.invalid.length > 0 && (
              <Alert variant="warning" className="mb-3">
                <FaExclamationTriangle className="me-2" />
                <strong>
                  Se encontraron {validationResults.invalid.length} registros
                  con errores.
                </strong>
                <br />
                Estos registros no serán importados. Puedes corregir los errores
                en tu archivo y volver a intentar.
              </Alert>
            )}

            <div style={{maxHeight: "400px", overflowY: "auto"}}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Teléfono</th>
                    <th>Email</th>
                    <th>Organización</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResults.valid.map((row, index) => (
                    <tr key={index} className="table-success">
                      <td>{row.rowNumber}</td>
                      <td>{row.nombre}</td>
                      <td>{row.apellido}</td>
                      <td>{row.telefono}</td>
                      <td>{row.email}</td>
                      <td>{row.organizacion || "Sin organización"}</td>
                      <td>
                        <Badge bg="success">
                          <FaCheck className="me-1" />
                          Válido
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {validationResults.invalid.map((row, index) => (
                    <tr key={`invalid-${index}`} className="table-danger">
                      <td>{row.rowNumber}</td>
                      <td>{row.nombre || "-"}</td>
                      <td>{row.apellido || "-"}</td>
                      <td>{row.telefono || "-"}</td>
                      <td>{row.email || "-"}</td>
                      <td>{row.organizacion || "-"}</td>
                      <td>
                        <Badge bg="danger">
                          <FaExclamationTriangle className="me-1" />
                          {row.errors.join(", ")}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {importStep === 3 && (
          <div className="text-center py-4">
            <h5 className="mb-3">
              {processing
                ? "Importando contactos..."
                : "¡Importación completada!"}
            </h5>
            <ProgressBar
              now={progress}
              label={`${Math.round(progress)}%`}
              className="mb-3"
              style={{height: "25px"}}
            />
            <p className="text-muted">
              {processing
                ? `Procesando ${validationResults.valid.length} contactos...`
                : `Se importaron ${validationResults.valid.length} contactos exitosamente.`}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {importStep === 1 && (
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
        )}
        {importStep === 2 && (
          <>
            <Button variant="secondary" onClick={() => setImportStep(1)}>
              Volver
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={validationResults.valid.length === 0}
            >
              Importar {validationResults.valid.length} Contactos
            </Button>
          </>
        )}
        {importStep === 3 && !processing && (
          <Button variant="success" onClick={handleClose}>
            Finalizar
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkImportModal;
